import React, { useContext, useEffect, useRef } from 'react'
import { matchPath } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import ModalContext from 'contexts/ModalContext'
import fetchAccountBusiness from 'actions/businessTraveller/fetchBusinessAccount'
import { logDatadog } from 'services/datadogRum'
import BusinessTravellerPersonalAccountLinkingModal from 'businessTraveller/pages/personal-account-linking/BusinessTravellerPersonalAccountLinkingModal'
import {
  getPersonalAccountLinkingDismissedEmployeeId,
  setPersonalAccountLinkingDismissedEmployeeId,
} from 'businessTraveller/storage/businessTravellerStorage'
import { BUSINESS_TRAVELLER_ROOT_ROUTE } from 'components/App/Routes/routeConstants'

const HOME_ROUTE = `/:regionCode${BUSINESS_TRAVELLER_ROOT_ROUTE}`

/**
 * Invite acceptance activates the employee before the wizard's linking step, so a dropout stays
 * unlinked (LEBT-3489): prompt them on the home page until they link; a dismissal lasts an hour or until the next sign-in.
 */
function BusinessTravellerPersonalAccountLinkingPrompt() {
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const isDedicatedTenantRegistrationEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.lebtDedicatedTenantRegistration)

  const accessToken = useAppSelector(state => state.auth.accessToken)
  const currentBusinessAccount = useAppSelector(state => state.businessTraveller.currentBusinessAccount)
  const pathname = useAppSelector(state => state.router.location.pathname)

  const { business, employee } = currentBusinessAccount?.status === 'success' ? currentBusinessAccount : {}

  const needsLinking = isDedicatedTenantRegistrationEnabled &&
    !!accessToken &&
    !!business &&
    !!employee?.activatedAt &&
    !employee.creditCustomerId &&
    !!matchPath(pathname, { path: HOME_ROUTE, exact: true })

  const isOpenRef = useRef(false)

  useEffect(() => {
    if (!needsLinking || !business || !employee || isOpenRef.current || getPersonalAccountLinkingDismissedEmployeeId() === employee.id) {
      return
    }
    isOpenRef.current = true

    logDatadog('Personal account linking prompt shown', { businessId: business.id, employeeId: employee.id }, 'info')

    showModal(<BusinessTravellerPersonalAccountLinkingModal business={business} employee={employee} />)
      .then(() => {
        isOpenRef.current = false
        setPersonalAccountLinkingDismissedEmployeeId(employee.id)
        dispatch(fetchAccountBusiness({ refetch: true }))
      })
  }, [needsLinking, business, employee, dispatch, showModal])

  return null
}

export default BusinessTravellerPersonalAccountLinkingPrompt
